import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/* eslint-disable import/dynamic-import-chunkname */
import React, { useContext, useEffect, Suspense } from 'react';
import { IntlProvider } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { ComponentErrorBoundary, GenericErrorBoundary, IntegrationErrorHandlerBoundary } from '../error-boundary';
import { PortalProvider } from '../context/portal';
import { WidthProvider, RendererContext } from '../context';
import { Loader } from '../web-runtime/loader';
import StyleErrorBoundary from '../error-boundary/StyleErrorBoundary';
import { RendererNextLegacy } from './RendererWithComponents';
import FrameCountProvider from '../provider/FrameCountProvider';
import { getExtensionType, parseExtensionId } from '../utils';
import { EnvironmentContext } from '../context';
import { createSrcFromExtension } from '../custom-ui/iframe/utils';
import { useForgeUiAnalyticsEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { ForgeDeprecationMessage, SANDBOX_DEPRECATION_ERROR } from './ForgeDeprecationMessage';
import { privateAppExtensionList } from './uiKit1RetirementPrivateAppExceptionList';
import { useSendRendererRenderedAnalytics } from '../utils/useSendRendererRenderedAnalytics';
import { ForgeExtensionPointModal } from './ForgeExtensionPointModal';
var getLoadingComponent = function getLoadingComponent(loadingComponent) {
  if (loadingComponent) {
    // Wrapping loadingComponent in a valid react element (fragment) for change `fix-react-18-concurrent-upgrade-infinite-rendering`
    // ReactNode type is not necessarily a valid React element, unable to change this type as different extension points pass in various loadingComponent types
    return /*#__PURE__*/React.createElement(React.Fragment, null, loadingComponent);
  }
  return /*#__PURE__*/React.createElement(Loader, null);
};
var getAppDomainName = function getAppDomainName(extension, environment) {
  var parsedExtension = extension && extension !== null && extension !== void 0 && extension.id ? parseExtensionId(extension.id) : undefined;
  if (parsedExtension) {
    var iframeSrc = createSrcFromExtension(parsedExtension.appId, extension, environment, '');
    var iframeUrl = new URL(iframeSrc);
    return iframeUrl.host;
  }
  return;
};
var RendererNextLatest = /*#__PURE__*/React.lazy(function () {
  return /* webpackChunkName: "forge-ui-renderer-with-components" */(
    import('./RendererWithComponents').then(function (module) {
      return {
        default: module.RendererNextLatest
      };
    })
  );
});
var shouldRenderDeprecationErrorMessage = function shouldRenderDeprecationErrorMessage(appId, isNative, forgeReactMajorVersion, error) {
  if (error === SANDBOX_DEPRECATION_ERROR) {
    return true;
  }

  // Currently supported - UI Kit 2 and @forge/react >=10
  if (isNative && forgeReactMajorVersion) {
    return false;
  }

  // Deprecated - UI Kit 2 @forge/react < 10.
  if (isNative && !forgeReactMajorVersion) {
    return true;
  }

  // Deprecated - UI Kit 1
  if (!fg('platform_disable_ui_kit_1')) {
    return false;
  }
  var uiKit1AppHasExtension = appId ? appId in privateAppExtensionList : false;

  // Excluded from deprecation UI Kit 1 apps
  if (uiKit1AppHasExtension && fg('platform_enable_ui_kit_1_extension_list')) {
    return false;
  }

  // Non excluded UI Kit 1 apps
  return true;
};

// This extra component exists so errors from the "render" function are caught in the an error boundary
// Also the error boundary requires a suspense fallback
var RendererNextWithProviders = function RendererNextWithProviders(props) {
  var extension = props.extension,
    extensionData = props.extensionData,
    forgeDoc = props.forgeDoc,
    dispatch = props.dispatch,
    locale = props.locale,
    client = props.client,
    error = props.error;
  var defaultDispatch = React.useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), []);
  var forgeReactMajorVersion = forgeDoc === null || forgeDoc === void 0 ? void 0 : forgeDoc.forgeReactMajorVersion;
  var environment = useContext(EnvironmentContext);
  var domainName = getAppDomainName(extension, environment);
  var _useForgeUiAnalyticsE = useForgeUiAnalyticsEvent(),
    trackExtensionLoaded = _useForgeUiAnalyticsE.trackExtensionLoaded;
  useEffect(function () {
    var _extensionData$macro, _extensionData$macro2;
    trackExtensionLoaded({
      renderType: props.isNative ? 'UIKit' : 'UIKit1',
      forgeEnvironment: extension === null || extension === void 0 ? void 0 : extension.environmentType,
      extensionType: getExtensionType(extension),
      isConfiguring: extensionData === null || extensionData === void 0 || (_extensionData$macro = extensionData.macro) === null || _extensionData$macro === void 0 ? void 0 : _extensionData$macro.isConfiguring,
      isInserting: extensionData === null || extensionData === void 0 || (_extensionData$macro2 = extensionData.macro) === null || _extensionData$macro2 === void 0 ? void 0 : _extensionData$macro2.isInserting
    });
    // Adding the dependencies, including trackExtensionLoaded, causes this effect to fire too often
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (forgeDoc && shouldRenderDeprecationErrorMessage(extension.appId, props.isNative, forgeReactMajorVersion, error)) {
    return props.modalExtension && fg('platform_modal_ext_error_and_deprecation_message') ? /*#__PURE__*/React.createElement(ForgeExtensionPointModal, {
      modalExtension: props.modalExtension
    }, /*#__PURE__*/React.createElement(ForgeDeprecationMessage, {
      client: client,
      extension: extension,
      error: error
    })) : /*#__PURE__*/React.createElement(ForgeDeprecationMessage, {
      client: client,
      extension: extension,
      error: error
    });
  }

  // true while fetching initial forgeDoc
  if (fg('fix-react-18-concurrent-upgrade-infinite-rendering')) {
    if (props.loading && !forgeDoc) {
      return getLoadingComponent(props.loadingComponent);
    }
  }
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: getLoadingComponent(props.loadingComponent)
  }, /*#__PURE__*/React.createElement(GenericErrorBoundary, {
    dispatch: dispatch,
    extension: extension
  }, /*#__PURE__*/React.createElement(ComponentErrorBoundary, {
    dispatch: dispatch,
    extension: extension
  }, /*#__PURE__*/React.createElement(StyleErrorBoundary, null, /*#__PURE__*/React.createElement(IntegrationErrorHandlerBoundary, {
    onError: props.onError
  }, /*#__PURE__*/React.createElement(WidthProvider, null, /*#__PURE__*/React.createElement(IntlProvider, {
    locale: locale !== null && locale !== void 0 ? locale : 'en',
    defaultLocale: "en"
  }, /*#__PURE__*/React.createElement(PortalProvider, null, /*#__PURE__*/React.createElement(RendererContext.Provider, {
    value: {
      egress: extension === null || extension === void 0 ? void 0 : extension.egress,
      properties: extension === null || extension === void 0 ? void 0 : extension.properties,
      forgeEnvironment: extension === null || extension === void 0 ? void 0 : extension.environmentType,
      forgeReactMajorVersion: forgeReactMajorVersion,
      appDomainName: domainName,
      extensionType: extension === null || extension === void 0 ? void 0 : extension.type
    }
  }, props.isNative && forgeReactMajorVersion && forgeReactMajorVersion >= 10 ? /*#__PURE__*/React.createElement(FrameCountProvider, null, /*#__PURE__*/React.createElement(RenderTrackingWrapper, null, /*#__PURE__*/React.createElement(RendererNextLatest, _extends({}, props, {
    dispatch: dispatch !== null && dispatch !== void 0 ? dispatch : defaultDispatch
  })))) : /*#__PURE__*/React.createElement(RendererNextLegacy, _extends({}, props, {
    dispatch: dispatch !== null && dispatch !== void 0 ? dispatch : defaultDispatch
  })))))))))));
};
var RenderTrackingWrapper = function RenderTrackingWrapper(_ref2) {
  var children = _ref2.children;
  if (fg('forge-ui-track-ui-kit-renderer-rendered-event')) {
    return /*#__PURE__*/React.createElement(RenderTrackingWrapperActual, null, children);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
};
var RenderTrackingWrapperActual = function RenderTrackingWrapperActual(_ref3) {
  var children = _ref3.children;
  useSendRendererRenderedAnalytics();
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
};
export default (function (props) {
  return /*#__PURE__*/React.createElement(RendererNextWithProviders, props);
});